<template>
  <case-detail-base :case-data="caseData"></case-detail-base>
</template>

<script>
import CaseDetailBase from './CaseDetailBase.vue'

export default {
  name: 'CaseVoicePrint',
  components: {CaseDetailBase},
  data() {
    return {
      // ===== id=2：AI声纹与视觉融合智能检测系统 =====
      // 图片目录：public/customerCase/case2/feat1~9.gif（真实 GIF 到位前，用 svg 字段的示意图占位）
      caseData: {
        id: 2,
        imgBase: '/customerCase/case2/feat',
        title: 'AI声纹与视觉融合智能检测系统',
        subtitle: '将声音特征分析、AI声纹识别、机器视觉、设备控制与数据追溯整合到同一软件平台，让每一次装配结果都有声音与视觉的双重依据。',
        summary: '面向工业装配、产品检验和产线质量管控场景，本系统将<strong>声音特征分析、AI声纹识别、机器视觉、设备控制与数据追溯</strong>整合到同一个平台。系统不仅判断产品最终结果，还能分析装配过程中的声学特征、视觉状态与设备信号，为异常发现、质量追溯与工艺优化提供持续的数据支撑——从单点判断升级为多证据融合，从固定阈值升级为数据驱动阈值。',
        tags: ['AI声纹识别', '机器视觉', '多源融合检测', '产线集成', '本地化部署'],
        caseLabel: '客户案例',
        caseMainTitle: '从"人听目视"到"声视融合"',
        caseMainDesc: '传统装配检测依赖人工听音、目视判断或单一传感器结果，易受经验、状态与环境波动影响；本系统以多源信息融合与自动化控制，将检测转化为可配置、可记录、可追溯、可优化的数字化流程。',
        challengesTitle: '上线前的痛点',
        challenges: ['人工听音、目视判断受经验与状态影响', '单一传感器判据易漏判、误判', '检测阈值依赖经验，难持续优化', '检测结果数据孤岛、难以追溯'],
        solutionsTitle: '我们的交付',
        solutions: ['声学特征 + AI声纹 + 视觉结果联合判断', '历史数据驱动 P1-P5 区间自动推算', 'Modbus / SQL Server / MES 产线集成', '本地部署、离线运行、全程数据留痕'],
        resultsTitle: '方案收益',
        results: ['漏判误判减少，结果更稳定透明', '阈值不再依赖经验，按目标良率优化', '形成识别-采集-判断-控制-追溯闭环', '模块化扩展，随工艺逐步接入新设备'],
        archNodes: [
          {title: '采集端', sub: '麦克风 / 工业采集卡 / 摄像头'},
          {title: '融合判断引擎', sub: '声学特征 + AI声纹 + 视觉'},
          {title: '控制与追溯', sub: 'Modbus / PLC / SQL Server'},
        ],
        archNote: '系统支持<strong>完全本地化部署</strong>，可运行于产线内网与离线环境，所有检测数据与模型均在域内，满足数据安全与合规要求。',
        archTags: [
          {abbr: 'P1-P5', desc: '五项声学特征联合良率'},
          {abbr: 'Transformer', desc: '声纹分类模型本地推理'},
          {abbr: 'Modbus TCP', desc: '与 PLC 产线通信'},
          {abbr: '机器视觉', desc: '多摄像头实时检测'},
          {abbr: '数据追溯', desc: 'CSV/图片/视频/数据库'},
        ],
        features: [
          {
            title: '多源融合检测',
            svg: 'fusion',
            desc: '同步采集声音、视觉与设备状态数据，结合 P1-P5 声学特征、AI声纹与视觉结果进行综合判断，减少单一判据的漏判与误判。'
          },
          {title: 'AI声纹识别', svg: 'voiceprint', desc: '基于短时音频特征与 Transformer 模型进行声音分类，融合梅尔频谱、MFCC、色度与 RMS 能量多维特征，支持本地部署与流式事件检测。'},
          {title: 'P1-P5 声学特征分析', svg: 'p1p5', desc: '从采集信号中提取脉冲峰值、宽度、面积、面积宽度比、频谱质心等五项关键特征，各特征独立配置上下限，联合计算良率。'},
          {title: '智能良率区间计算', svg: 'yieldRange', desc: '根据历史数据与目标良率自动推算 P1-P5 推荐上下限，使用联合通过率而非单列统计，计算后自动刷新每日良率柱形图。'},
          {title: '机器视觉检测', svg: 'vision', desc: '多摄像头接入与实时画面显示，支持裸手手势检测与目标检测模型识别，视觉触发采集与声音检测同步，结果颜色标识并可存图录像。'},
          {title: '自动化产线控制', svg: 'plc', desc: '支持手动测试、自动测试与产线触发三种模式，兼容扫码枪、读卡器、三色灯与串口设备，测试结果与状态信号实时输出，无缝接入现有产线节奏。'},
          {title: '数据追溯与质量管理', svg: 'trace', desc: '检测记录自动保存 CSV、图片与视频，支持按条件查询筛选、趋势图与良率统计，并可基于历史数据重新分析，持续优化检测区间。'},
          {title: '用户与安全管理', svg: 'security', desc: 'L1/L2/L3 三级权限体系，密码采用 PBKDF2-SHA256 哈希，数据库口令支持 DPAPI 加密与环境变量注入，未登录隐藏用户管理功能。'},
          {title: '设备配置与维护', svg: 'device', desc: '支持麦克风自检、传感器校准、摄像头与视觉区域配置、产品型号与测试配方管理，配合作业指导窗口与三色状态灯。'},
        ],
        techs: [
          {
            title: 'PySide6 工业桌面应用',
            desc: '面向工业现场日常操作设计，稳定适配产线日常使用习惯。'
          },
          {
            title: '双通道 48kHz 音频采集',
            desc: '环形缓冲 + 阈值触发式数据截取，兼顾连续采集与事件分析。'
          },
          {title: '多特征 AI 声纹模型本地推理', desc: 'Transformer 声纹分类模型支持模型包验证、离线 WAV 测试与流式服务运行，无需上云。'},
          {
            title: '多线程视觉处理',
            desc: '支持实时画面与检测结果更新，视觉触发与声音检测流程同步。'
          },
          {title: '异步数据库查询', desc: '数据库查询异步执行，避免网络异常造成界面卡死。'},
          {
            title: '模块化可扩展架构',
            desc: '可接入采集卡、麦克风、摄像头、串口、Modbus 与 SQL Server，各模块随工艺与产品线逐步扩展。'
          },
        ],
        info: {
          deliver: '声纹与视觉融合检测桌面系统 + 模型训练工具 + Modbus/数据库集成组件',
          scene: '汽车零部件、3C电子组件装配与异响检测，卡扣/插接/敲击/脉冲类工艺检测，以及 PLC/MES 联动的自动化产线',
          security: '完全本地化部署，支持内网与离线运行，模型与数据均不出域',
          deploy: '本地化桌面部署（支持离线运行）',
          cycle: '标准场景 2 周内完成部署上线',
          service: '7×24 技术支持 / 驻场交付 / 模型训练赋能',
        },
        // 硬件清单（并入「方案信息」区块展示）
        hardwares: [
          {name: '主机柜', qty: 1, unit: '套'},
          {name: '工控机', qty: 1, unit: '套'},
          {name: '显示器 21 寸', qty: 1, unit: '套'},
          {name: '显示器 23.8 寸', qty: 1, unit: '套'},
          {name: '无线麦克风', qty: 3, unit: '套'},
          {name: '麦克风多功能套件', qty: 1, unit: '套'},
          {name: '键鼠', qty: 1, unit: '套'},
          {name: '刷卡器', qty: 1, unit: '套'},
          {name: '可变焦高清摄像机', qty: 2, unit: '套'},
          {name: '千兆纯铜网线 50m', qty: 1, unit: '条'},
          {name: '千兆纯铜网线 8m', qty: 1, unit: '条'},
        ],
        // 产线通讯：Modbus TCP（并入「技术亮点」区块展示）
        modbus: {
          svg: 'modbus',
          desc: '声纹机与线体 PLC 通过 Modbus TCP 协议交互：声纹机运行 Modbus 服务器，内部开辟寄存器地址，线体通过读写这些地址完成测试请求、结果回收与状态确认。',
          registers: [
            {name: '声纹机心跳'},
            {name: '包体类型'},
            {name: 'FID 码'},
            {name: '包体码'},
            {name: '线体请求测试信号'},
            {name: '声纹机测试完成信号'},
          ],
          tip: '若线体查询声纹机离线，应优先检查声纹机的 Modbus 服务器是否开启。',
        },
        // 双数据库部署（并入「技术亮点」区块展示）
        databases: {
          svg: 'database',
          title: '双数据库：本地库 + 线体库',
          desc: '检测结果采用双通道写入，既留存在声纹机本地便于追溯与离线分析，也同步至线体数据库供产线/MES 侧使用。',
          items: [
            {name: '本地数据库', desc: '部署在声纹机电脑中，保存全量检测记录、音频与图像，支持离线查询与历史数据重新分析。'},
            {name: '线体数据库', desc: '部署在线体电脑中，供产线侧与 MES 读取，支持断线重连与手动补传。'},
          ],
        },
        cta: {
          title: '想看看它如何运转？',
          desc: '预约一次在线演示，我们用真实系统为你展示声纹识别、视觉复核与产线联动的完整检测闭环。'
        },
      },
    };
  },
};
</script>
